import { PureComponent, ReactElement } from 'react';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';

import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import LaptopAndPhone from 'dibs-icons/exports/LaptopAndPhone';
import PhoneRingingInHand from 'dibs-icons/exports/PhoneRingingInHand';
import NumberInputDisplay from 'dibs-icons/exports/NumberInputDisplay';
import PhoneRinging from 'dibs-icons/exports/PhoneRinging';

import CallDealerForm from '../CallDealerForm/CallDealerForm';
import CallInitiatedSuccess from '../CallInitiatedSuccess/CallInitiatedSuccess';
import { onCallModalOpen, onCallModalSuccess, CallSellerModalLocation } from '../../tracking';
import initiateCallMutation from '../../mutations/InitiateCall';
import styles from './styles.scss';
import { CallDealerModal_viewer } from './__generated__/CallDealerModal_viewer.graphql';
import { CallDealerModal_seller } from './__generated__/CallDealerModal_seller.graphql';
import { CallDealerModal_item } from './__generated__/CallDealerModal_item.graphql';
import { CallDealerModal_conversation } from './__generated__/CallDealerModal_conversation.graphql';

type Props = {
    relay: RelayProp;
    userId?: string;
    isOpened: boolean;
    hasUserId?: boolean;
    onClose: () => void;
    onAuthRequired?: (
        onAuthSuccess: (buyerId: string) => Promise<void>,
        onAuthFailure: () => void
    ) => void;
    viewer: CallDealerModal_viewer;
    seller: CallDealerModal_seller | null;
    item: CallDealerModal_item | null;
    conversation: CallDealerModal_conversation | null;
    location: CallSellerModalLocation;
};

type State = {
    name: string;
    countryCode: string;
    phoneNumber: string;
    savePhoneNumber: boolean;
    phoneCallbackEnabled: boolean;
    submitting: boolean;
    success: boolean;
    error?: ReactElement | string;
};

const defaultCountryCode = '1';
const numbersOnlyRegex = /^\d+$/;
const containsLettersRegex = /[a-z]/i;

const unknownServiceError = (
    <FormattedMessage
        id="dc.buyerInitiateCall.unknown_service_error"
        defaultMessage="Unknown error occurred. Please try again later."
    />
);

const invalidInput = (
    <FormattedMessage
        id="dc.buyerInitiateCall.invalid_input"
        defaultMessage="Phone number format is invalid. Include a country code and the correct number of digits."
    />
);

const initialState = ({ viewer, conversation }: Props): State => {
    const name = viewer.viewerUser?.profile?.displayName || '';

    const existingPhone = !!viewer.primaryPhone?.isActive;
    const phoneCallbackEnabled = conversation?.currentConversationMember?.phoneCallbackEnabled;

    return {
        name,
        countryCode:
            (existingPhone && viewer.primaryPhone?.countryCodeNumber) || defaultCountryCode,
        phoneNumber: (existingPhone && viewer.primaryPhone?.formattedPhoneNumber) || '',
        savePhoneNumber: !existingPhone,
        phoneCallbackEnabled: phoneCallbackEnabled !== false,
        submitting: false,
        success: false,
        error: undefined,
    };
};

const headerSteps = [
    {
        icon: <LaptopAndPhone className={styles.icon} />,
        key: 'LaptopAndPhone',
        copy: (
            <FormattedMessage
                id="dc.buyerInitiateCall.haveYourPhone"
                defaultMessage="Have your phone handy."
            />
        ),
    },
    {
        icon: <NumberInputDisplay className={styles.icon} />,
        key: 'NumberInput',
        copy: (
            <FormattedMessage
                id="dc.buyerInitiateCall.enterNumber"
                defaultMessage="Enter your phone number."
            />
        ),
    },
    {
        icon: <PhoneRingingInHand className={styles.icon} />,
        key: 'PhoneRingingInHand',
        copy: (
            <FormattedMessage
                id="dc.buyerInitiateCall.getCall"
                defaultMessage="You’ll get a call, connecting you with the seller."
            />
        ),
    },
];

export class CallDealerModal extends PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = initialState(props);

        if (!props.userId && !props.onAuthRequired) {
            throw new Error(
                'No userId prop given. Must have a handler for when auth is then required.'
            );
        }
        if (props.isOpened && props.location) {
            onCallModalOpen(props.location);
        }

        this.initiateCall = this.initiateCall.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onFailure = this.onFailure.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props): void {
        const previousPhone = this.props.viewer.primaryPhone;
        const currentPhone = nextProps.viewer.primaryPhone;
        const updatePhone = !previousPhone && currentPhone;

        if (!this.state.submitting && updatePhone) {
            this.setState(() => initialState(nextProps));
        }
        if (!this.props.isOpened && nextProps.isOpened && nextProps.location) {
            onCallModalOpen(nextProps.location);
        }
    }

    async initiateCall(buyerId: string): Promise<void> {
        const { seller, item, relay, location } = this.props;
        const {
            name,
            phoneNumber,
            countryCode,
            savePhoneNumber,
            phoneCallbackEnabled,
        } = this.state;

        try {
            const response = await initiateCallMutation(relay.environment, {
                buyerId,
                saveName: false,
                savePhoneNumber,
                buyerName: name,
                // strip out non-numeric characters
                buyerNumber: phoneNumber.replace(/\D/g, ''),
                buyerCountryCode: Number(countryCode),
                itemId: item?.serviceId,
                itemName: item?.title,
                dealerId: seller?.serviceId || '',
                phoneCallbackEnabled,
            });

            const { success, errors } = response?.initiateCall || {};

            if (success) {
                this.setState({
                    success: true,
                    submitting: false,
                    savePhoneNumber: false,
                });
                if (location) {
                    onCallModalSuccess(location, phoneCallbackEnabled);
                }
            } else if (errors && errors.length) {
                const firstError = errors[0];
                this.setState({
                    error: firstError?.message || unknownServiceError,
                    submitting: false,
                });
            } else {
                this.setState({ error: unknownServiceError, submitting: false });
            }
        } catch (error) {
            this.onFailure();
        }
    }

    onSubmit(): void {
        const { userId, onAuthRequired } = this.props;
        const { phoneNumber, countryCode } = this.state;

        const invalidCountryCode = !numbersOnlyRegex.test(countryCode);
        const invalidPhoneNumber = containsLettersRegex.test(phoneNumber);

        if (invalidCountryCode || invalidPhoneNumber) {
            this.setState({ error: invalidInput, submitting: false });
            return;
        }

        this.setState({ submitting: true, error: undefined });

        if (!userId) {
            onAuthRequired?.(
                this.initiateCall,
                () => this.setState({ submitting: false }) // if auth fails
            );
            return;
        }

        this.initiateCall(userId);
    }

    onFailure(): void {
        this.setState({ error: unknownServiceError, submitting: false });
    }

    onClose(): void {
        this.setState({ success: false });
        this.props.onClose();
    }

    render(): ReactElement {
        const { isOpened, seller } = this.props;
        const dealerLocation = seller?.shippingAddress?.displayCityStateCountry;

        let title;
        let header;
        let prompt;
        let subtitle;
        if (this.state.success) {
            title = (
                <FormattedMessage
                    id="dc.buyerInitiateCall.modalSuccessHeader"
                    data-tn="call-initiated"
                    defaultMessage="Expect Our Call"
                />
            );
            header = (
                <div className={styles.wrapper}>
                    <BarHeader title={title} subtitle={subtitle} />
                    <div className={styles.confirmationHeader}>
                        <PhoneRinging className={styles.iconRinging} />
                        <div className={styles.stepDescription}>
                            <FormattedMessage
                                id="dc.buyerInitiateCall.call_initiated_description"
                                defaultMessage="When you answer, you’ll hear ringing until the seller picks up."
                            />
                        </div>
                    </div>
                </div>
            );
            prompt = <CallInitiatedSuccess />;
        } else {
            title = (
                <FormattedMessage
                    id="dc.buyerInitiateCall.modalFormHeader"
                    defaultMessage="Set Up a Call with the Seller"
                />
            );
            subtitle = dealerLocation ? (
                <FormattedMessage
                    id="dc.buyerInitiateCall.modalFormSubHeader"
                    defaultMessage="Located in {dealerLocation}"
                    values={{ dealerLocation }}
                />
            ) : null;

            header = (
                <div className={styles.wrapper}>
                    <BarHeader title={title} subtitle={subtitle} />
                    <div className={styles.steps}>
                        {headerSteps.map((step, key) => {
                            return (
                                <div className={styles.step} key={step.key}>
                                    {step.icon}
                                    <div className={styles.stepNumber}>{key + 1}.</div>
                                    <div className={styles.stepDescription}>{step.copy}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
            prompt = (
                <CallDealerForm
                    showAllowCallbackPrompt
                    phoneNumber={this.state.phoneNumber}
                    countryCode={this.state.countryCode}
                    phoneCallbackEnabled={this.state.phoneCallbackEnabled}
                    submitting={this.state.submitting}
                    error={this.state.error}
                    setPhoneNumber={phoneNumber =>
                        this.setState({
                            phoneNumber,
                            savePhoneNumber: true,
                            error: undefined,
                        })
                    }
                    setCountryCode={countryCode =>
                        this.setState({
                            countryCode,
                            savePhoneNumber: true,
                            error: undefined,
                        })
                    }
                    setPhoneCallbackEnabled={enabled =>
                        this.setState({ phoneCallbackEnabled: enabled })
                    }
                    onSubmit={this.onSubmit}
                />
            );
        }

        return (
            <ModalContainer isOpen={isOpened} onClose={this.onClose} modalPosition="center">
                <ModalCloseButton onClick={this.onClose} />
                {header}
                {prompt}
            </ModalContainer>
        );
    }
}

export default createFragmentContainer(CallDealerModal, {
    conversation: graphql`
        fragment CallDealerModal_conversation on Conversation {
            currentConversationMember: currentMember(currentUserId: $userId)
            @include(if: $hasUserId) {
                phoneCallbackEnabled
            }
        }
    `,
    item: graphql`
        fragment CallDealerModal_item on Item {
            serviceId
            title
        }
    `,
    seller: graphql`
        fragment CallDealerModal_seller on Seller {
            serviceId
            shippingAddress {
                displayCityStateCountry
            }
        }
    `,
    viewer: graphql`
        fragment CallDealerModal_viewer on Viewer {
            viewerUser: user(userId: $userId) @include(if: $hasUserId) {
                profile {
                    displayName(preventEmailFallback: true)
                }
            }
            primaryPhone(userId: $userId) @include(if: $hasUserId) {
                countryCodeNumber
                formattedPhoneNumber
                isActive: hasStatus(status: "ACTIVE")
            }
        }
    `,
});
